import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  BuyerFocusPackageDTO,
  BuyerFocusPackageItemDTO,
  BuyerProductRequestDTO,
  SkuDTO,
} from "@mydailydirect/api-client";
import {
  formatPaise,
  Card,
  CardContent,
  Button,
  SearchBar,
  Dialog,
  DialogHeader,
  DialogFooter,
  useToast,
  Badge,
} from "@mydailydirect/ui";
import {
  Sparkles,
  TrendingDown,
  TrendingUp,
  Minus,
  Plus,
  Trash2,
  PlusCircle,
  Zap,
  BookmarkPlus,
  Flame,
  ChevronDown,
  ChevronUp,
  Package,
  Layers,
  CheckSquare,
  Square,
  CheckCheck,
  Clock,
  Filter,
  Grid,
  ListFilter,
  Check,
  Lightbulb,
  Send,
} from "lucide-react";

export const ORDER_FREQUENCIES = [
  { id: "DAILY", label: "Daily", short: "Daily", icon: "☀️", description: "Everyday staple", color: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  { id: "EVERY_2_DAYS", label: "Alternate Days", short: "2 Days", icon: "⚡", description: "Every 48 hours", color: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  { id: "TWICE_A_WEEK", label: "Twice a Week", short: "2x/Wk", icon: "🔄", description: "Twice a week", color: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/30" },
  { id: "WEEKLY", label: "Weekly", short: "Weekly", icon: "📅", description: "Regular weekly order", color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  { id: "BI_WEEKLY", label: "Bi-Weekly (15d)", short: "15 Days", icon: "🗓️", description: "Every 15 days", color: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/30" },
  { id: "MONTHLY", label: "Monthly", short: "Monthly", icon: "📦", description: "Monthly order", color: "bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/30" },
  { id: "AS_NEEDED", label: "As Needed", short: "On Demand", icon: "🎯", description: "As needed", color: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30" },
] as const;

export type OrderFrequencyId = (typeof ORDER_FREQUENCIES)[number]["id"];

export function parseItemFrequency(notes?: string | null): { frequency: OrderFrequencyId; cleanNotes: string } {
  if (!notes) return { frequency: "WEEKLY", cleanNotes: "" };
  const match = notes.match(/^FREQ:([A-Z0-9_]+)(?:\s*\|\s*(.*))?$/i);
  if (match) {
    const freqId = match[1].toUpperCase() as OrderFrequencyId;
    const valid = ORDER_FREQUENCIES.some((f) => f.id === freqId);
    return {
      frequency: valid ? freqId : "WEEKLY",
      cleanNotes: match[2]?.trim() || "",
    };
  }
  return { frequency: "WEEKLY", cleanNotes: notes.trim() };
}

export function serializeItemFrequency(frequency: OrderFrequencyId, cleanNotes?: string | null): string {
  const clean = (cleanNotes || "").trim();
  return clean ? `FREQ:${frequency} | ${clean}` : `FREQ:${frequency}`;
}

export interface BuyerFocusPackageSectionProps {
  retailerId: number;
  retailerName?: string;
  client: any;
  /** Callback when user clicks "Load to Order" / "Add Focus Items to Basket" */
  onLoadToOrder?: (items: { skuId: number; qty: number; packId?: number | null }[]) => void;
  /** Current items in order/cart (to offer "Save Current Basket as Focus Package") */
  currentCartLines?: { skuId: number; qty: number }[];
  /** Available SKUs for adding new items to package */
  availableSkus?: SkuDTO[];
  /** Custom title override */
  title?: string;
  /** Counter/trigger to programmatically open the Suggest New SKU modal from parent */
  openSuggestSkuTrigger?: number;
}

const SUGGEST_CATEGORIES = [
  "Rice",
  "Pulses & Dals",
  "Edible Oil",
  "Sugar & Jaggery",
  "Flour, Maida & Sooji",
  "Spices & Masala",
  "Dry Fruits & Nuts",
  "Bakery & HoReCa",
  "Other Staples",
];

export function BuyerFocusPackageSection({
  retailerId,
  retailerName,
  client,
  onLoadToOrder,
  currentCartLines = [],
  availableSkus = [],
  title,
  openSuggestSkuTrigger = 0,
}: BuyerFocusPackageSectionProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<"ASSORTMENT" | "MULTI_SELECT" | "SUGGEST_SKU">("ASSORTMENT");

  // Suggest New SKU form state
  const [suggestProductName, setSuggestProductName] = useState("");
  const [suggestCategory, setSuggestCategory] = useState("Rice");
  const [suggestExpectedQty, setSuggestExpectedQty] = useState("5");
  const [suggestTargetPriceRupees, setSuggestTargetPriceRupees] = useState("");
  const [suggestFrequency, setSuggestFrequency] = useState<OrderFrequencyId>("WEEKLY");
  const [suggestComments, setSuggestComments] = useState("");

  useEffect(() => {
    if (openSuggestSkuTrigger > 0) {
      setModalTab("SUGGEST_SKU");
      setIsAddModalOpen(true);
    }
  }, [openSuggestSkuTrigger]);

  // Filtering on active focus items
  const [frequencyFilter, setFrequencyFilter] = useState<"ALL" | OrderFrequencyId>("ALL");

  // Multi-select state for Catalog Picker
  const [selectedSkuIds, setSelectedSkuIds] = useState<Set<number>>(new Set());
  const [selectedSkuQuantities, setSelectedSkuQuantities] = useState<Record<number, number>>({});
  const [selectedSkuFrequencies, setSelectedSkuFrequencies] = useState<Record<number, OrderFrequencyId>>({});
  const [addCategoryFilter, setAddCategoryFilter] = useState("ALL");
  const [skuSearchQuery, setSkuSearchQuery] = useState("");
  const [localItemQuantities, setLocalItemQuantities] = useState<Record<number, number>>({});
  const [localItemFrequencies, setLocalItemFrequencies] = useState<Record<number, OrderFrequencyId>>({});

  // 1. Fetch Focus Package for this retailer
  const packageQuery = useQuery({
    queryKey: ["focusPackage", retailerId],
    queryFn: () => client.focusPackages.get(retailerId),
    enabled: Number.isFinite(retailerId) && retailerId > 0,
  });

  const focusPackage: BuyerFocusPackageDTO | null = packageQuery.data ?? null;

  // 1b. Fetch Suggested / Missing Product Requests for this shop
  const shopRequestsQuery = useQuery({
    queryKey: ["buyerProductRequests", retailerId],
    queryFn: async () => {
      const list: BuyerProductRequestDTO[] = await client.focusPackages.listRequests(retailerId);
      return (list ?? []).filter((r) => !r.retailerId || Number(r.retailerId) === Number(retailerId));
    },
    enabled: Number.isFinite(retailerId) && retailerId > 0,
  });

  const shopRequests: BuyerProductRequestDTO[] = shopRequestsQuery.data ?? [];

  // 2. Fetch starter templates
  const templatesQuery = useQuery({
    queryKey: ["focusPackageTemplates"],
    queryFn: () => client.focusPackages.getTemplates(),
    enabled: isTemplateModalOpen,
  });

  // 3. Fallback catalog fetch if availableSkus prop was not passed
  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    enabled: availableSkus.length === 0,
  });

  const catalogSkus: SkuDTO[] = useMemo(() => {
    return availableSkus.length > 0 ? availableSkus : (skusQuery.data ?? []);
  }, [availableSkus, skusQuery.data]);

  // Keep track of quantities in local state for live edits
  const items: BuyerFocusPackageItemDTO[] = useMemo(() => {
    if (!focusPackage?.items) return [];
    return focusPackage.items;
  }, [focusPackage]);

  const getItemQty = (item: BuyerFocusPackageItemDTO) => {
    return localItemQuantities[item.skuId] ?? item.defaultQty ?? 1;
  };

  const getItemFrequency = (item: BuyerFocusPackageItemDTO): OrderFrequencyId => {
    return localItemFrequencies[item.skuId] ?? parseItemFrequency(item.notes).frequency;
  };

  const setItemQty = (skuId: number, qty: number) => {
    setLocalItemQuantities((prev) => ({
      ...prev,
      [skuId]: Math.max(1, qty),
    }));
  };

  // Mutations
  const updateItemMutation = useMutation({
    mutationFn: ({
      skuId,
      qty,
      notes,
    }: {
      skuId: number;
      qty: number;
      notes?: string | null;
    }) => client.focusPackages.updateItem(retailerId, skuId, qty, undefined, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: (skuId: number) => client.focusPackages.removeItem(retailerId, skuId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      toast({
        title: "Item Removed",
        description: "Removed product from buyer's focus pack.",
        variant: "info",
      });
    },
  });

  const addBatchMutation = useMutation({
    mutationFn: (itemsToAdd: { skuId: number; defaultQty: number; notes?: string }[]) => {
      return client.focusPackages.addItems(retailerId, itemsToAdd);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      setIsAddModalOpen(false);
      setSelectedSkuIds(new Set());
      setSelectedSkuQuantities({});
      setSelectedSkuFrequencies({});
      toast({
        title: "Focus Package Updated! 🎯",
        description: `Added ${selectedSkuIds.size} regular staple products to ${retailerName || "buyer"}'s pack.`,
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Could not add focus items",
        description: err instanceof Error ? err.message : "Failed to update package",
        variant: "error",
      });
    },
  });

  const addItemMutation = useMutation({
    mutationFn: ({
      skuId,
      qty,
      frequency,
    }: {
      skuId: number;
      qty: number;
      frequency: OrderFrequencyId;
    }) => {
      return client.focusPackages.addItem(retailerId, {
        skuId,
        defaultQty: qty,
        notes: serializeItemFrequency(frequency),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      toast({
        title: "Added to Focus Pack! 🎯",
        description: "Assortment item added to buyer's recurring staples.",
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to add item",
        description: err instanceof Error ? err.message : "Error",
        variant: "error",
      });
    },
  });

  const saveCurrentCartAsPackage = useMutation({
    mutationFn: () => {
      const payloadItems = currentCartLines.map((l) => ({
        skuId: l.skuId,
        defaultQty: l.qty,
        notes: serializeItemFrequency("WEEKLY"),
      }));
      return client.focusPackages.save(retailerId, {
        name: `${retailerName || "Buyer"} Regular Basket`,
        description: `Customized regular recurring package saved on ${new Date().toLocaleDateString()}`,
        items: payloadItems,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      toast({
        title: "Saved as Focus Package! 🎯",
        description: "Current basket items are now this buyer's regular package.",
        variant: "success",
      });
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: (template: BuyerFocusPackageDTO) => {
      const payloadItems = (template.items || []).map((i: BuyerFocusPackageItemDTO) => ({
        skuId: i.skuId,
        defaultQty: i.defaultQty || 2,
        notes: i.notes || serializeItemFrequency("WEEKLY"),
      }));
      return client.focusPackages.save(retailerId, {
        name: `${retailerName || "Buyer"} ${template.name}`,
        description: template.description || "Applied from starter template",
        items: payloadItems,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      setIsTemplateModalOpen(false);
      toast({
        title: "Template Applied! 🚀",
        description: "Starter focus package loaded successfully.",
        variant: "success",
      });
    },
  });

  const submitSkuRequestMutation = useMutation({
    mutationFn: async () => {
      const nameTrimmed = suggestProductName.trim();
      if (!nameTrimmed) {
        throw new Error("Please enter the product / SKU name.");
      }
      const expectedQtyNum = parseInt(suggestExpectedQty, 10);
      const targetPriceNum = parseFloat(suggestTargetPriceRupees);
      const freqLabel = ORDER_FREQUENCIES.find((f) => f.id === suggestFrequency)?.label || suggestFrequency;
      const combinedComments = [
        `Cadence: ${freqLabel}`,
        suggestComments.trim(),
      ]
        .filter(Boolean)
        .join(" | ");

      return client.focusPackages.submitRequest({
        retailerId,
        productName: nameTrimmed,
        category: suggestCategory || "Other Staples",
        expectedQty: Number.isFinite(expectedQtyNum) && expectedQtyNum > 0 ? expectedQtyNum : 1,
        targetPricePaise:
          Number.isFinite(targetPriceNum) && targetPriceNum > 0
            ? Math.round(targetPriceNum * 100)
            : null,
        comments: combinedComments || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buyerProductRequests", retailerId] });
      queryClient.invalidateQueries({ queryKey: ["buyerProductRequests"] });
      setSuggestProductName("");
      setSuggestExpectedQty("5");
      setSuggestTargetPriceRupees("");
      setSuggestComments("");
      setIsAddModalOpen(false);
      toast({
        title: "New SKU Suggestion Submitted! 💡",
        description: `Logged missing SKU demand for ${retailerName || "this shop"}.`,
        variant: "success",
      });
    },
    onError: (err) => {
      toast({
        title: "Could not submit SKU suggestion",
        description: err instanceof Error ? err.message : "Failed to submit request",
        variant: "error",
      });
    },
  });

  const openSuggestSkuModal = (prefillName?: string) => {
    if (prefillName) {
      setSuggestProductName(prefillName);
    }
    setModalTab("SUGGEST_SKU");
    setIsAddModalOpen(true);
  };

  // Calculate live total based on current stepped quantities
  const liveTotalPaise = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = getItemQty(item);
      return sum + (item.pricePaise || 0) * qty;
    }, 0);
  }, [items, localItemQuantities]);

  const liveBagCount = useMemo(() => {
    return items.reduce((sum, item) => sum + getItemQty(item), 0);
  }, [items, localItemQuantities]);

  // Dynamic category pills for Catalog Assortment
  const catalogCategories = useMemo(() => {
    const set = new Set<string>();
    catalogSkus.forEach((s) => {
      if (s.category && s.category.trim()) set.add(s.category.trim());
    });
    return Array.from(set).sort();
  }, [catalogSkus]);

  // Categories currently covered in buyer's focus pack
  const coveredCategories = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => {
      if (item.category && item.category.trim()) {
        set.add(item.category.trim());
      }
    });
    return set;
  }, [items]);

  // Missing catalog assortment categories
  const missingCategories = useMemo(() => {
    return catalogCategories.filter((c) => !coveredCategories.has(c));
  }, [catalogCategories, coveredCategories]);

  // Filter candidate SKUs for add modal (excluding already added)
  const existingSkuIds = useMemo(() => new Set(items.map((i) => i.skuId)), [items]);
  const candidateSkus = useMemo(() => {
    const q = skuSearchQuery.trim().toLowerCase();
    return catalogSkus
      .filter((s) => s.id && !existingSkuIds.has(s.id) && s.active !== false)
      .filter((s) => {
        if (addCategoryFilter !== "ALL" && s.category !== addCategoryFilter) return false;
        if (!q) return true;
        return (
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          (s.category ?? "").toLowerCase().includes(q) ||
          (s.brand ?? "").toLowerCase().includes(q)
        );
      });
  }, [catalogSkus, existingSkuIds, skuSearchQuery, addCategoryFilter]);

  // Catalog assortment grouped by category (for Assortment tab)
  const catalogAssortmentGrouped = useMemo(() => {
    const groups: Record<string, SkuDTO[]> = {};
    catalogSkus.forEach((sku) => {
      if (sku.active === false) return;
      const cat = sku.category?.trim() || "General Staples";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(sku);
    });
    return groups;
  }, [catalogSkus]);

  // Filtered active focus items based on frequency tab
  const displayedItems = useMemo(() => {
    if (frequencyFilter === "ALL") return items;
    return items.filter((item) => getItemFrequency(item) === frequencyFilter);
  }, [items, frequencyFilter, localItemFrequencies]);

  // Frequency counts
  const frequencyCounts = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((item) => {
      const f = getItemFrequency(item);
      map[f] = (map[f] || 0) + 1;
    });
    return map;
  }, [items, localItemFrequencies]);

  // Toggle single SKU selection in Catalog Picker
  const toggleSkuSelection = (skuId: number) => {
    setSelectedSkuIds((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) {
        next.delete(skuId);
      } else {
        next.add(skuId);
        if (!selectedSkuQuantities[skuId]) {
          setSelectedSkuQuantities((qPrev) => ({ ...qPrev, [skuId]: 2 }));
        }
        if (!selectedSkuFrequencies[skuId]) {
          setSelectedSkuFrequencies((fPrev) => ({ ...fPrev, [skuId]: "WEEKLY" }));
        }
      }
      return next;
    });
  };

  // Select all currently filtered candidate SKUs
  const handleSelectAllFiltered = () => {
    setSelectedSkuIds((prev) => {
      const next = new Set(prev);
      const newQuantities = { ...selectedSkuQuantities };
      const newFrequencies = { ...selectedSkuFrequencies };
      candidateSkus.forEach((s) => {
        if (s.id) {
          next.add(s.id);
          if (!newQuantities[s.id]) newQuantities[s.id] = 2;
          if (!newFrequencies[s.id]) newFrequencies[s.id] = "WEEKLY";
        }
      });
      setSelectedSkuQuantities(newQuantities);
      setSelectedSkuFrequencies(newFrequencies);
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedSkuIds(new Set());
  };

  const updateSelectedSkuQty = (skuId: number, qty: number) => {
    setSelectedSkuQuantities((prev) => ({
      ...prev,
      [skuId]: Math.max(1, qty),
    }));
  };

  const updateSelectedSkuFreq = (skuId: number, freq: OrderFrequencyId) => {
    setSelectedSkuFrequencies((prev) => ({
      ...prev,
      [skuId]: freq,
    }));
  };

  const handleConfirmBatchAdd = () => {
    if (selectedSkuIds.size === 0) return;
    const batchList = Array.from(selectedSkuIds).map((skuId) => ({
      skuId,
      defaultQty: selectedSkuQuantities[skuId] || 2,
      notes: serializeItemFrequency(selectedSkuFrequencies[skuId] || "WEEKLY"),
    }));
    addBatchMutation.mutate(batchList);
  };

  const handleUpdateItemFrequency = (item: BuyerFocusPackageItemDTO, freq: OrderFrequencyId) => {
    setLocalItemFrequencies((prev) => ({ ...prev, [item.skuId]: freq }));
    const currentQty = getItemQty(item);
    const parsed = parseItemFrequency(item.notes);
    updateItemMutation.mutate({
      skuId: item.skuId,
      qty: currentQty,
      notes: serializeItemFrequency(freq, parsed.cleanNotes),
    });
    toast({
      title: "Frequency Updated! 🔄",
      description: `${item.skuName} set to order ${ORDER_FREQUENCIES.find((f) => f.id === freq)?.label || freq}.`,
      variant: "success",
    });
  };

  const handleUpdateItemQty = (item: BuyerFocusPackageItemDTO, qty: number) => {
    const safeQty = Math.max(1, qty);
    setItemQty(item.skuId, safeQty);
    const freq = getItemFrequency(item);
    const parsed = parseItemFrequency(item.notes);
    updateItemMutation.mutate({
      skuId: item.skuId,
      qty: safeQty,
      notes: serializeItemFrequency(freq, parsed.cleanNotes),
    });
  };

  const openAssortmentForCategory = (cat: string) => {
    setAddCategoryFilter(cat);
    setModalTab("ASSORTMENT");
    setIsAddModalOpen(true);
  };

  if (packageQuery.isLoading) {
    return (
      <Card className="border border-indigo-500/20 bg-indigo-500/5 animate-pulse p-4">
        <div className="h-5 w-48 bg-indigo-200/50 dark:bg-indigo-900/50 rounded mb-2" />
        <div className="h-4 w-72 bg-muted rounded" />
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main Container Card */}
      <Card className="border-2 border-indigo-500/30 dark:border-indigo-500/40 bg-gradient-to-br from-indigo-50/40 via-background to-blue-50/20 dark:from-indigo-950/20 dark:via-background dark:to-blue-950/10 shadow-sm overflow-hidden transition-all">
        {/* Top Header Banner */}
        <div className="p-3.5 sm:p-4 border-b border-indigo-500/15 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-indigo-500/5">
          <div className="flex items-start sm:items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-indigo-600/20">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-2xs uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-indigo-600 text-white shadow-xs">
                  Buyer Focus Pack
                </span>
                <span className="text-xs font-semibold text-muted-foreground">
                  {items.length} Regular Staples
                </span>
                {items.some((i) => i.isLowest7d) && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 animate-pulse">
                    <Flame className="h-3 w-3 text-emerald-600 fill-emerald-500" /> 7-Day Low Alert
                  </span>
                )}
              </div>
              <h4 className="text-base font-extrabold text-foreground mt-0.5">
                {title || focusPackage?.name || "Regular Focus Package"}
              </h4>
              <p className="text-xs text-muted-foreground hidden sm:block">
                Buyer's recurring staple requirements with custom quantity & order frequency.
              </p>
            </div>
          </div>

          {/* Header Action Buttons */}
          <div className="flex items-center gap-2 self-end sm:self-center flex-wrap">
            {currentCartLines.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveCurrentCartAsPackage.mutate()}
                loading={saveCurrentCartAsPackage.isPending}
                className="h-8 text-xs font-bold gap-1.5 border-indigo-400/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/10"
                title="Save current basket items as this buyer's permanent regular package"
              >
                <BookmarkPlus className="h-3.5 w-3.5" /> Save Basket as Package
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => openSuggestSkuModal()}
              className="h-8 text-xs font-bold gap-1 border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 hover:bg-amber-500/20 shadow-xs"
            >
              <Lightbulb className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" /> + Suggest New SKU
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedSkuIds(new Set());
                setSelectedSkuQuantities({});
                setSelectedSkuFrequencies({});
                setModalTab("ASSORTMENT");
                setAddCategoryFilter("ALL");
                setIsAddModalOpen(true);
              }}
              className="h-8 text-xs font-bold gap-1 bg-indigo-600 hover:bg-indigo-700 text-white border-0 shadow-xs"
            >
              <PlusCircle className="h-3.5 w-3.5" /> Add from Assortment
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <CardContent className="p-3.5 sm:p-5 space-y-4">
            {/* 1. Assortment Coverage Banner */}
            <div className="p-3 rounded-xl bg-muted/40 border border-border/80 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Grid className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-xs font-extrabold text-foreground">
                    Catalog Assortment Coverage:
                  </span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                    {coveredCategories.size} of {catalogCategories.length} Categories in Pack
                  </span>
                </div>
                {missingCategories.length > 0 && (
                  <span className="text-2xs text-muted-foreground">
                    {missingCategories.length} missing staple categories available to add
                  </span>
                )}
              </div>

              {/* Assortment Category Chips */}
              <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                {catalogCategories.map((cat) => {
                  const isCovered = coveredCategories.has(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => openAssortmentForCategory(cat)}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1.5 ${
                        isCovered
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                          : "bg-background text-muted-foreground border-dashed border-indigo-300 dark:border-indigo-800 hover:border-indigo-500 hover:text-indigo-600"
                      }`}
                      title={isCovered ? `${cat} is already in focus pack` : `Click to add ${cat} from catalog assortment`}
                    >
                      {isCovered ? (
                        <Check className="h-3 w-3 text-emerald-600" />
                      ) : (
                        <Plus className="h-3 w-3 text-indigo-500" />
                      )}
                      <span>{cat}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {items.length === 0 ? (
              <div className="p-6 text-center border-2 border-dashed border-indigo-200 dark:border-indigo-900/50 rounded-xl space-y-3 bg-background/50">
                <Package className="h-10 w-10 mx-auto text-indigo-400" />
                <div>
                  <h5 className="text-sm font-bold text-foreground">No Focus Items Set Yet</h5>
                  <p className="text-xs text-muted-foreground max-w-md mx-auto mt-0.5">
                    Add the products this buyer regularly orders along with their proper quantity and replenishment frequency.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 pt-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      setSelectedSkuIds(new Set());
                      setSelectedSkuQuantities({});
                      setSelectedSkuFrequencies({});
                      setModalTab("ASSORTMENT");
                      setAddCategoryFilter("ALL");
                      setIsAddModalOpen(true);
                    }}
                    className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                  >
                    <PlusCircle className="h-3.5 w-3.5" /> Browse Catalog Assortment & Add
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openSuggestSkuModal()}
                    className="text-xs font-bold border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 hover:bg-amber-500/20 gap-1"
                  >
                    <Lightbulb className="h-3.5 w-3.5 text-amber-600" /> + Suggest New SKU
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* 2. Frequency Filter Tabs */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
                  <span className="text-2xs font-bold text-muted-foreground uppercase flex items-center gap-1 mr-1">
                    <Filter className="h-3 w-3" /> Cadence:
                  </span>
                  <button
                    type="button"
                    onClick={() => setFrequencyFilter("ALL")}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-all whitespace-nowrap text-xs ${
                      frequencyFilter === "ALL"
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                        : "bg-muted/60 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    All Cadences ({items.length})
                  </button>
                  {ORDER_FREQUENCIES.map((freq) => {
                    const count = frequencyCounts[freq.id] || 0;
                    if (count === 0 && frequencyFilter !== freq.id) return null;
                    const isActive = frequencyFilter === freq.id;
                    return (
                      <button
                        key={freq.id}
                        type="button"
                        onClick={() => setFrequencyFilter(freq.id)}
                        className={`px-2.5 py-1 rounded-lg font-bold transition-all whitespace-nowrap text-xs flex items-center gap-1 ${
                          isActive
                            ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs"
                            : "bg-muted/60 text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        <span>{freq.icon}</span>
                        <span>{freq.label}</span>
                        <span className="text-[10px] opacity-75">({count})</span>
                      </button>
                    );
                  })}
                </div>

                {/* 3. Responsive Items Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {displayedItems.map((item) => {
                    const currentQty = getItemQty(item);
                    const currentFreq = getItemFrequency(item);
                    const lineTotal = (item.pricePaise || 0) * currentQty;
                    const freqConfig = ORDER_FREQUENCIES.find((f) => f.id === currentFreq) || ORDER_FREQUENCIES[3];

                    return (
                      <div
                        key={item.skuId}
                        className="p-3 sm:p-3.5 rounded-xl border border-border/80 bg-background hover:border-indigo-500/40 hover:shadow-xs transition-all flex flex-col justify-between gap-3"
                      >
                        {/* Top: SKU Info + Price Trend Badge */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {item.category && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {item.category}
                                </span>
                              )}
                              {item.brand && (
                                <span className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400">
                                  {item.brand}
                                </span>
                              )}
                            </div>
                            <h5 className="text-sm font-bold text-foreground leading-snug line-clamp-1">
                              {item.skuName}
                            </h5>
                            <p className="text-xs font-medium text-muted-foreground">
                              Rate: <span className="font-bold text-foreground">{formatPaise(item.pricePaise)}</span>
                              {item.unit ? ` / ${item.unit}` : ""}
                            </p>
                          </div>

                          {/* 7-Day Price Trend Intelligence Badge */}
                          <div className="flex flex-col items-end shrink-0 gap-1">
                            {item.isLowest7d ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-white shadow-xs">
                                <Flame className="h-3 w-3 fill-white" /> Lowest in 7d!
                              </span>
                            ) : item.priceTrend === "UP" ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                                <TrendingUp className="h-3 w-3 text-amber-600" /> {item.trendLabel}
                              </span>
                            ) : item.priceTrend === "DOWN" ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                                <TrendingDown className="h-3 w-3 text-emerald-600" /> {item.trendLabel}
                              </span>
                            ) : (
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Stable Price
                              </span>
                            )}

                            {item.trendPercent !== 0 && (
                              <span
                                className={`text-[10px] font-mono font-bold ${
                                  item.trendPercent < 0
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-amber-600 dark:text-amber-400"
                                }`}
                              >
                                {item.trendPercent > 0 ? `+${item.trendPercent}%` : `${item.trendPercent}%`}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Middle: Frequency & Cadence Selector */}
                        <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/30 border border-border/60 text-xs">
                          <span className="text-2xs font-bold text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3 text-indigo-600" /> Frequency:
                          </span>
                          <div className="flex items-center gap-1.5">
                            <select
                              value={currentFreq}
                              onChange={(e) => handleUpdateItemFrequency(item, e.target.value as OrderFrequencyId)}
                              className="h-7 px-2 text-xs font-bold rounded-md bg-background border border-border text-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                              {ORDER_FREQUENCIES.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.icon} {f.label} ({f.description})
                                </option>
                              ))}
                            </select>
                            <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded border ${freqConfig.color}`}>
                              {freqConfig.short}
                            </span>
                          </div>
                        </div>

                        {/* Bottom Row: Proper Quantity Stepper + Line Total + Trash */}
                        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/60">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-muted-foreground">
                              Order Qty:
                            </span>
                            <div className="flex items-center border border-border rounded-lg bg-muted/30 p-0.5">
                              <button
                                type="button"
                                onClick={() => handleUpdateItemQty(item, currentQty - 1)}
                                className="h-7 w-7 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors font-bold"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <input
                                type="number"
                                min={1}
                                value={currentQty}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  setItemQty(item.skuId, isNaN(val) ? 1 : val);
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value, 10);
                                  handleUpdateItemQty(item, isNaN(val) ? 1 : val);
                                }}
                                className="w-12 text-center text-xs font-mono font-bold bg-transparent border-0 focus:outline-none focus:ring-0 p-0"
                              />
                              <button
                                type="button"
                                onClick={() => handleUpdateItemQty(item, currentQty + 1)}
                                className="h-7 w-7 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors font-bold"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {item.unit && (
                              <span className="text-2xs text-muted-foreground font-medium">
                                {item.unit}s
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-xs font-extrabold font-mono text-foreground">
                              {formatPaise(lineTotal)}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeItemMutation.mutate(item.skuId)}
                              className="text-muted-foreground hover:text-destructive p-1 rounded transition-colors"
                              title="Remove from Focus Package"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer: Package Summary + One-Click Order Button */}
                <div className="p-3.5 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/60 flex flex-col sm:flex-row items-center justify-between gap-3 mt-2">
                  <div className="space-y-0.5 text-center sm:text-left">
                    <span className="text-2xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                      Standard Routine Requirement
                    </span>
                    <div className="flex items-baseline gap-2 justify-center sm:justify-start flex-wrap">
                      <span className="text-lg font-black font-mono text-foreground">
                        {formatPaise(liveTotalPaise)}
                      </span>
                      <span className="text-xs font-semibold text-muted-foreground">
                        ({liveBagCount} units across {items.length} staples)
                      </span>
                    </div>
                  </div>

                  {onLoadToOrder && (
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          const orderItems = items.map((i) => ({
                            skuId: i.skuId,
                            qty: getItemQty(i),
                          }));
                          onLoadToOrder(orderItems);
                        }}
                        className="w-full sm:w-auto text-xs font-black bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/20 gap-1.5 h-9 px-4"
                      >
                        <Zap className="h-4 w-4" /> Load Package to Order ({items.length} Items)
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 4. Shop-Level Suggested / Requested Missing SKUs Section (Shown when requests exist) */}
            {shopRequests.length > 0 && (
              <div className="p-3.5 rounded-xl bg-amber-500/5 dark:bg-amber-950/20 border border-amber-500/25 space-y-2.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <span className="text-xs font-extrabold text-foreground">
                      Suggested / Requested New SKUs
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                      {shopRequests.length} Requested
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  {shopRequests.map((req, idx) => (
                    <div
                      key={req.id ?? idx}
                      className="p-2.5 rounded-lg border border-amber-500/25 bg-background flex flex-col justify-between gap-1.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-[10px] font-extrabold uppercase text-amber-700 dark:text-amber-400">
                            {req.category || "Staple Request"}
                          </span>
                          <p className="text-xs font-bold text-foreground truncate">
                            {req.productName}
                          </p>
                        </div>
                        <Badge variant="warning" className="text-[9px] font-bold shrink-0">
                          {req.status || "OPEN"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-2xs text-muted-foreground flex-wrap gap-1">
                        <span>
                          Expected Qty: <strong className="text-foreground">{req.expectedQty || 1} bags/units</strong>
                        </span>
                        {req.targetPricePaise ? (
                          <span>
                            Target Rate: <strong className="text-foreground">{formatPaise(req.targetPricePaise)}</strong>
                          </span>
                        ) : null}
                      </div>
                      {req.comments && (
                        <p className="text-[11px] text-muted-foreground bg-muted/40 px-2 py-1 rounded truncate">
                          {req.comments}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Modal: Add Products to Focus List (Catalog Assortment, Multi-Select, & Suggest New SKU) */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        size="3xl"
      >
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 w-full pr-6">
            <div className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-indigo-600 shrink-0" />
              <div>
                <h3 className="text-sm sm:text-base font-bold text-foreground">
                  Catalog Assortment & Focus Pack
                </h3>
                <p className="text-2xs text-muted-foreground">
                  Select products or suggest a new missing SKU for {retailerName || "buyer"}.
                </p>
              </div>
            </div>

            {/* Modal Tab Switcher */}
            <div className="grid grid-cols-3 sm:flex items-center gap-1 p-1 rounded-lg bg-muted border border-border text-2xs sm:text-xs w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setModalTab("ASSORTMENT")}
                className={`px-2 py-1.5 rounded font-bold transition-all flex items-center justify-center gap-1 whitespace-nowrap ${
                  modalTab === "ASSORTMENT"
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Grid className="h-3.5 w-3.5 shrink-0" /> Assortment
              </button>
              <button
                type="button"
                onClick={() => setModalTab("MULTI_SELECT")}
                className={`px-2 py-1.5 rounded font-bold transition-all flex items-center justify-center gap-1 whitespace-nowrap ${
                  modalTab === "MULTI_SELECT"
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ListFilter className="h-3.5 w-3.5 shrink-0" /> Multi-Select
              </button>
              <button
                type="button"
                onClick={() => setModalTab("SUGGEST_SKU")}
                className={`px-2 py-1.5 rounded font-bold transition-all flex items-center justify-center gap-1 whitespace-nowrap ${
                  modalTab === "SUGGEST_SKU"
                    ? "bg-amber-600 text-white shadow-xs"
                    : "text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
                }`}
              >
                <Lightbulb className="h-3.5 w-3.5 shrink-0" /> + Suggest SKU
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2 flex-1 min-h-0">
          {modalTab !== "SUGGEST_SKU" && (
            <>
              {/* Search Bar */}
              <SearchBar
                value={skuSearchQuery}
                onChange={setSkuSearchQuery}
                placeholder="Search staples by name, brand, or code..."
                className="w-full"
              />

              {/* Category Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-2xs">
                <button
                  type="button"
                  onClick={() => setAddCategoryFilter("ALL")}
                  className={`px-2.5 py-1 rounded-full font-bold transition-all whitespace-nowrap ${
                    addCategoryFilter === "ALL"
                      ? "bg-indigo-600 text-white shadow-xs"
                      : "bg-muted/70 text-muted-foreground hover:bg-muted"
                  }`}
                >
                  All Assortment ({catalogSkus.length})
                </button>
                {catalogCategories.map((cat) => {
                  const count = catalogSkus.filter((s) => s.category === cat).length;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setAddCategoryFilter(cat)}
                      className={`px-2.5 py-1 rounded-full font-bold transition-all whitespace-nowrap ${
                        addCategoryFilter === cat
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "bg-muted/70 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* TAB 3: SUGGEST NEW MISSING SKU FORM */}
          {modalTab === "SUGGEST_SKU" && (
            <div className="p-3.5 sm:p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-4">
              <div className="flex items-start gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-foreground">
                    Suggest New SKU for {retailerName || "this Shop"}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Log a product, brand, or pack size that this shop regularly buys so our procurement team can add it to the catalog.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 space-y-1">
                  <label className="text-xs font-bold text-foreground">
                    Product Name / Brand / Pack Size <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={suggestProductName}
                    onChange={(e) => setSuggestProductName(e.target.value)}
                    placeholder="e.g., Venugopal Maida 50kg Bag, Bullet Rice 26kg, Ruchi Gold 15L Tin..."
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Category</label>
                  <select
                    value={suggestCategory}
                    onChange={(e) => setSuggestCategory(e.target.value)}
                    className="w-full h-9 px-2.5 rounded-lg border border-border bg-background text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {SUGGEST_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Order Frequency / Cadence</label>
                  <select
                    value={suggestFrequency}
                    onChange={(e) => setSuggestFrequency(e.target.value as OrderFrequencyId)}
                    className="w-full h-9 px-2.5 rounded-lg border border-border bg-background text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {ORDER_FREQUENCIES.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.icon} {f.label} ({f.description})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">Expected Order Qty (Bags / Units)</label>
                  <input
                    type="number"
                    min={1}
                    value={suggestExpectedQty}
                    onChange={(e) => setSuggestExpectedQty(e.target.value)}
                    placeholder="e.g., 5"
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs font-mono font-bold focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-foreground">
                    Buyer's Current / Target Price (₹ per unit, optional)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={suggestTargetPriceRupees}
                    onChange={(e) => setSuggestTargetPriceRupees(e.target.value)}
                    placeholder="e.g., 1450"
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-xs font-mono font-bold focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="sm:col-span-2 space-y-1">
                  <label className="text-xs font-bold text-foreground">
                    Additional Notes (Quality, Competitor Supplier, Credit Terms)
                  </label>
                  <textarea
                    rows={2}
                    value={suggestComments}
                    onChange={(e) => setSuggestComments(e.target.value)}
                    placeholder="e.g., Currently buying from local mandi at ₹1,420/bag every Monday..."
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: CATALOG ASSORTMENT BY CATEGORY VIEW */}
          {modalTab === "ASSORTMENT" && (
            <div className="space-y-4">
              {Object.entries(catalogAssortmentGrouped)
                .filter(([cat]) => addCategoryFilter === "ALL" || cat === addCategoryFilter)
                .map(([cat, skus]) => {
                  const filteredSkus = skus.filter((s) => {
                    const q = skuSearchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      s.name.toLowerCase().includes(q) ||
                      s.code.toLowerCase().includes(q) ||
                      (s.brand ?? "").toLowerCase().includes(q)
                    );
                  });

                  if (filteredSkus.length === 0) return null;

                  const inPackCount = filteredSkus.filter((s) => s.id && existingSkuIds.has(s.id)).length;

                  return (
                    <div key={cat} className="space-y-2 border border-border rounded-xl p-3 bg-muted/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-black uppercase tracking-wider text-foreground">
                            {cat}
                          </h4>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            {filteredSkus.length} products
                          </span>
                        </div>
                        <span className="text-2xs font-semibold text-muted-foreground">
                          {inPackCount > 0 ? (
                            <span className="text-emerald-600 font-bold">
                              ✓ {inPackCount} in Focus Pack
                            </span>
                          ) : (
                            <span className="text-amber-600">None in pack yet</span>
                          )}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {filteredSkus.map((sku) => {
                          if (!sku.id) return null;
                          const inPack = existingSkuIds.has(sku.id);
                          const existingItem = items.find((i) => i.skuId === sku.id);
                          const parsedExistingFreq = existingItem ? parseItemFrequency(existingItem.notes) : null;

                          return (
                            <div
                              key={sku.id}
                              className={`p-3 rounded-xl border transition-all flex flex-col justify-between gap-2.5 ${
                                inPack
                                  ? "border-emerald-500/30 bg-emerald-500/5 dark:bg-emerald-950/20"
                                  : "border-border bg-card hover:border-indigo-500/40"
                              }`}
                            >
                              <div>
                                <div className="flex items-start justify-between gap-1">
                                  <div className="min-w-0">
                                    {sku.brand && (
                                      <span className="text-[10px] font-extrabold uppercase text-indigo-600 dark:text-indigo-400 block truncate">
                                        {sku.brand}
                                      </span>
                                    )}
                                    <h5 className="text-xs font-bold text-foreground leading-tight line-clamp-2">
                                      {sku.name}
                                    </h5>
                                  </div>
                                  {inPack && (
                                    <Badge variant="success" className="text-[9px] font-bold shrink-0">
                                      In Pack
                                    </Badge>
                                  )}
                                </div>

                                <div className="mt-1 flex items-center justify-between text-2xs text-muted-foreground">
                                  <span>Rate: <strong className="text-foreground">{formatPaise(sku.pricePaise)}</strong>{sku.unit ? `/${sku.unit}` : ""}</span>
                                  {sku.code && <span className="font-mono text-[10px]">{sku.code}</span>}
                                </div>
                              </div>

                              {inPack ? (
                                <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 text-2xs font-semibold flex items-center justify-between">
                                  <span>Routine: {existingItem?.defaultQty} {sku.unit || "units"}</span>
                                  <span>🔄 {parsedExistingFreq?.frequency}</span>
                                </div>
                              ) : (
                                <div className="pt-2 border-t border-border/60 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-muted-foreground">Qty:</span>
                                    <input
                                      type="number"
                                      min={1}
                                      defaultValue={sku.category?.toLowerCase().includes("oil") ? 5 : 2}
                                      id={`assort-qty-${sku.id}`}
                                      className="w-12 h-6 text-center text-xs font-mono font-bold border border-border rounded bg-background"
                                    />
                                    <select
                                      id={`assort-freq-${sku.id}`}
                                      defaultValue="WEEKLY"
                                      className="h-6 text-[10px] font-bold border border-border rounded bg-background px-1"
                                    >
                                      {ORDER_FREQUENCIES.map((f) => (
                                        <option key={f.id} value={f.id}>
                                          {f.short}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <Button
                                    size="sm"
                                    variant="primary"
                                    loading={addItemMutation.isPending}
                                    onClick={() => {
                                      const qtyEl = document.getElementById(`assort-qty-${sku.id}`) as HTMLInputElement;
                                      const freqEl = document.getElementById(`assort-freq-${sku.id}`) as HTMLSelectElement;
                                      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 2) : 2;
                                      const freq = (freqEl?.value || "WEEKLY") as OrderFrequencyId;
                                      addItemMutation.mutate({ skuId: sku.id!, qty, frequency: freq });
                                    }}
                                    className="h-7 px-2.5 text-2xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                                  >
                                    <Plus className="h-3 w-3 mr-0.5" /> Add
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* TAB 2: FAST MULTI-SELECT CATALOG VIEW */}
          {modalTab === "MULTI_SELECT" && (
            <div className="space-y-3">
              {/* Quick Toolbar: Select All / Deselect All / Counter */}
              <div className="flex items-center justify-between px-1 text-xs text-muted-foreground font-semibold">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSelectAllFiltered}
                    disabled={candidateSkus.length === 0}
                    className="text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-bold disabled:opacity-40"
                  >
                    <CheckCheck className="h-3.5 w-3.5" /> Select All Filtered ({candidateSkus.length})
                  </button>
                  {selectedSkuIds.size > 0 && (
                    <button
                      type="button"
                      onClick={handleClearSelection}
                      className="text-muted-foreground hover:text-foreground font-medium"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                <span className="font-bold text-foreground">
                  {selectedSkuIds.size} items selected
                </span>
              </div>

              {/* Candidate Multi-Select List */}
              <div className="space-y-2 border rounded-xl p-2.5 bg-muted/20">
                {candidateSkus.length === 0 ? (
                  <div className="p-8 text-center space-y-1">
                    <Package className="h-8 w-8 text-muted-foreground mx-auto opacity-50 mb-2" />
                    <p className="text-xs font-bold text-foreground">
                      {skuSearchQuery ? "No matching products found." : "All active products already in package."}
                    </p>
                    <p className="text-2xs text-muted-foreground">Try clearing search or picking a different category filter.</p>
                  </div>
                ) : (
                  candidateSkus.map((sku) => {
                    if (!sku.id) return null;
                    const isSelected = selectedSkuIds.has(sku.id);
                    const currentQty = selectedSkuQuantities[sku.id] || 2;
                    const currentFreq = selectedSkuFrequencies[sku.id] || "WEEKLY";

                    return (
                      <div
                        key={sku.id}
                        onClick={() => sku.id && toggleSkuSelection(sku.id)}
                        className={`p-3 rounded-xl border text-left cursor-pointer transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-50/80 dark:bg-indigo-950/50 shadow-xs ring-1 ring-indigo-500/50"
                            : "border-border bg-background hover:bg-muted/40 hover:border-border/80"
                        }`}
                      >
                        {/* Checkbox + Info */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="shrink-0 text-indigo-600 dark:text-indigo-400">
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 fill-indigo-600/20" />
                            ) : (
                              <Square className="h-5 w-5 text-muted-foreground/60" />
                            )}
                          </div>

                          <div className="space-y-0.5 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {sku.brand && (
                                <span className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400 uppercase">
                                  {sku.brand}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground">{sku.category}</span>
                            </div>
                            <p className="text-xs font-bold text-foreground truncate">{sku.name}</p>
                            <div className="flex items-center gap-2 text-2xs text-muted-foreground font-mono">
                              <span>Rate: <strong>{formatPaise(sku.pricePaise)}</strong>{sku.unit ? ` / ${sku.unit}` : ""}</span>
                              {sku.availableQty !== undefined && (
                                <span>· Avail: {sku.availableQty}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Quantity and Frequency Controls when selected */}
                        {isSelected && (
                          <div
                            className="flex items-center gap-2 shrink-0 bg-background border border-indigo-300 dark:border-indigo-800 rounded-lg p-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-1">
                              <span className="text-2xs text-muted-foreground font-medium">Qty:</span>
                              <button
                                type="button"
                                onClick={() => sku.id && updateSelectedSkuQty(sku.id, Math.max(1, currentQty - 1))}
                                className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted font-bold"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-8 text-center text-xs font-mono font-bold">
                                {currentQty}
                              </span>
                              <button
                                type="button"
                                onClick={() => sku.id && updateSelectedSkuQty(sku.id, currentQty + 1)}
                                className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted font-bold"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>

                            <select
                              value={currentFreq}
                              onChange={(e) => sku.id && updateSelectedSkuFreq(sku.id, e.target.value as OrderFrequencyId)}
                              className="h-6 text-[10px] font-bold border border-border rounded bg-muted/30 px-1"
                            >
                              {ORDER_FREQUENCIES.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.short}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between items-center">
          <div className="text-xs text-muted-foreground hidden sm:block">
            {modalTab === "SUGGEST_SKU" ? (
              <span>Log missing product demand for this shop so procurement can onboard it.</span>
            ) : selectedSkuIds.size > 0 ? (
              <span><strong>{selectedSkuIds.size} items</strong> selected for addition.</span>
            ) : (
              <span>Browse assortment or click items to select.</span>
            )}
          </div>

          <div className="flex items-center gap-2 justify-end w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(false)} className="h-9 text-xs font-semibold">
              Cancel
            </Button>
            {modalTab === "SUGGEST_SKU" && (
              <Button
                variant="primary"
                size="sm"
                loading={submitSkuRequestMutation.isPending}
                disabled={!suggestProductName.trim()}
                onClick={() => submitSkuRequestMutation.mutate()}
                className="flex-1 sm:flex-initial h-9 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs px-4 gap-1.5 shadow-xs"
              >
                <Send className="h-3.5 w-3.5" /> Save & Submit Suggestion
              </Button>
            )}
            {modalTab === "MULTI_SELECT" && selectedSkuIds.size > 0 && (
              <Button
                variant="primary"
                size="sm"
                loading={addBatchMutation.isPending}
                onClick={handleConfirmBatchAdd}
                className="flex-1 sm:flex-initial h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 shadow-xs"
              >
                Add {selectedSkuIds.size} Selected Items
              </Button>
            )}
          </div>
        </DialogFooter>
      </Dialog>

      {/* Modal: Pick Starter Template */}
      <Dialog open={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            <h3 className="text-base font-bold text-foreground">
              Apply Pre-Curated Starter Template
            </h3>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">
            Select an industry-standard package template to populate this buyer's regular requirement in 1 click.
          </p>

          <div className="space-y-2.5">
            {(templatesQuery.data ?? []).map((tmpl: BuyerFocusPackageDTO) => (
              <div
                key={tmpl.id}
                className="p-3.5 rounded-xl border border-border bg-background hover:border-indigo-500/50 hover:bg-indigo-50/20 dark:hover:bg-indigo-950/20 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="space-y-1">
                  <span className="text-2xs font-extrabold uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
                    {tmpl.retailerName || "Industry Standard"}
                  </span>
                  <h5 className="text-sm font-extrabold text-foreground">{tmpl.name}</h5>
                  <p className="text-xs text-muted-foreground">{tmpl.description}</p>
                  <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 block">
                    Includes {tmpl.items?.length || 0} staples · Estimated {formatPaise(tmpl.totalEstimatedPaise)}
                  </span>
                </div>

                <Button
                  size="sm"
                  variant="primary"
                  loading={applyTemplateMutation.isPending}
                  onClick={() => applyTemplateMutation.mutate(tmpl)}
                  className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                >
                  Apply Package
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setIsTemplateModalOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
